import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, Check, Link2Off } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';

export const GoogleConnectCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    nexaApi.get('/google/status').then(({ data }) => setStatus(data)).catch(() => setStatus({ connected: false }));
  };

  useEffect(() => {
    load();
    // Detect ?google=connected after returning from consent
    const url = new URL(window.location.href);
    if (url.searchParams.get('google') === 'connected') {
      toast.success('Google Calendar connected');
      url.searchParams.delete('google');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } else if (url.searchParams.get('google') === 'error') {
      toast.error('Google connect failed — try again');
      url.searchParams.delete('google');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
    const onFocus = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await nexaApi.get('/google/auth-url');
      window.location.href = data.authorization_url;
    } catch {
      toast.error('Could not start Google sign-in');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await nexaApi.delete('/google/disconnect');
      toast.success('Disconnected');
      load();
    } catch {
      toast.error('Could not disconnect');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-google-card">
      <div className="flex items-center gap-2 mb-3">
        <CalendarDays className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
        <span className="text-xs tracking-[0.2em] uppercase text-white/40">Google Calendar</span>
      </div>
      {status === null ? (
        <div className="w-4 h-4 border-2 border-sky-400/30 border-t-sky-400 rounded-full animate-spin" />
      ) : status.connected ? (
        <>
          <p className="text-sm text-white/70 flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-400" strokeWidth={2.5} />
            Connected as <span className="font-mono text-white/85">{status.email || '—'}</span>
          </p>
          <p className="text-xs text-white/40 mt-1">
            NEXA sees your calendar and can add or delete events from chat.
          </p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={disconnect}
            disabled={busy}
            data-testid="nexa-google-disconnect"
            className="mt-4 w-full h-11 rounded-xl border border-white/15 text-sm text-white/80 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Link2Off className="w-3.5 h-3.5" strokeWidth={2} /> Disconnect
          </motion.button>
        </>
      ) : (
        <>
          <p className="text-sm text-white/60 mb-4">
            Sync your primary Google Calendar + Tasks. NEXA will show today&apos;s schedule on Today,
            plan around your meetings in chat, and can add or delete events when you ask.
          </p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={connect}
            disabled={busy}
            data-testid="nexa-google-connect"
            className="w-full h-12 rounded-xl bg-white text-black font-semibold disabled:opacity-50"
          >
            {busy ? 'Opening Google…' : 'Connect Google Calendar'}
          </motion.button>
        </>
      )}
    </div>
  );
};
